import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Table, 
  Tag, 
  Button, 
  Modal,
  Typography,
  Space,
  Tooltip,
  Alert,
  Spin,
  Divider,
  List,
  Timeline
} from 'antd';
import { 
  BarChartOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  EyeOutlined,
  StopOutlined,
  RedoOutlined,
  DownloadOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { 
  getBulkSMSAnalytics, 
  subscribeToCampaignUpdates,
  cancelBulkSMSCampaign,
  retryFailedMessages,
  MESSAGE_STATUS
} from '../../services/enterpriseBulkSMSService';

const { Title, Text, Paragraph } = Typography;

/**
 * Bulk SMS Analytics Dashboard
 * Real-time monitoring of SMS campaigns with comprehensive metrics
 */
const BulkSMSAnalyticsDashboard = ({ campaignId, visible, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Real-time updates
  useEffect(() => {
    if (!campaignId || !visible) return;

    let unsubscribe = null;

    const initializeAnalytics = async () => {
      try {
        setLoading(true);
        const data = await getBulkSMSAnalytics(campaignId);
        setAnalytics(data);

        // Subscribe to real-time updates
        unsubscribe = subscribeToCampaignUpdates(campaignId, (snapshot) => {
          if (snapshot.exists()) {
            const campaignData = { id: snapshot.id, ...snapshot.data() };
            setAnalytics(prev => prev ? { ...prev, campaign: campaignData } : null);
          }
        });

      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAnalytics();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [campaignId, visible]);

  // Refresh analytics data
  const handleRefresh = async () => {
    if (!campaignId) return;

    try {
      setRefreshing(true);
      const data = await getBulkSMSAnalytics(campaignId);
      setAnalytics(data);
    } catch (error) {
      console.error('Error refreshing analytics:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Cancel campaign
  const handleCancelCampaign = async () => {
    Modal.confirm({
      title: 'Cancel Campaign',
      content: 'Are you sure you want to cancel this campaign? Queued messages will not be sent.',
      okText: 'Cancel Campaign',
      okType: 'danger',
      onOk: async () => {
        try {
          setActionLoading(true);
          await cancelBulkSMSCampaign(campaignId);
          await handleRefresh();
        } catch (error) {
          console.error('Error cancelling campaign:', error);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Retry failed messages
  const handleRetryFailed = async () => {
    try {
      setActionLoading(true);
      await retryFailedMessages(campaignId);
      await handleRefresh();
    } catch (error) {
      console.error('Error retrying failed messages:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case MESSAGE_STATUS.SENT:
      case MESSAGE_STATUS.DELIVERED:
        return 'success';
      case MESSAGE_STATUS.FAILED:
        return 'error';
      case MESSAGE_STATUS.PROCESSING:
      case MESSAGE_STATUS.RETRYING:
        return 'processing';
      case MESSAGE_STATUS.QUEUED:
      case MESSAGE_STATUS.SCHEDULED:
        return 'default';
      case MESSAGE_STATUS.CANCELLED:
        return 'warning';
      default:
        return 'default';
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!analytics) return 0;
    const { statusBreakdown, totalMessages } = analytics;
    const processed = statusBreakdown.sent + statusBreakdown.failed + statusBreakdown.delivered;
    return totalMessages > 0 ? Math.round((processed / totalMessages) * 100) : 0;
  };

  // Generate status breakdown data for table
  const getStatusTableData = () => {
    if (!analytics) return [];

    return Object.entries(analytics.statusBreakdown).map(([status, count]) => ({
      key: status,
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count,
      percentage: analytics.totalMessages > 0 ? ((count / analytics.totalMessages) * 100).toFixed(1) : '0'
    }));
  };

  // Generate location breakdown data
  const getLocationTableData = () => {
    if (!analytics) return [];

    return Object.entries(analytics.locationBreakdown)
      .sort(([,a], [,b]) => b - a)
      .map(([location, count]) => ({
        key: location,
        location,
        count,
        percentage: analytics.totalMessages > 0 ? ((count / analytics.totalMessages) * 100).toFixed(1) : '0'
      }));
  };

  // Table columns for status breakdown
  const statusColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Space>
          <Tag color={getStatusColor(record.key)}>{status}</Tag>
        </Space>
      )
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right'
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (percentage) => `${percentage}%`
    }
  ];

  // Table columns for location breakdown
  const locationColumns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location'
    },
    {
      title: 'Recipients',
      dataIndex: 'count',
      key: 'count',
      align: 'right'
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (percentage) => `${percentage}%`
    }
  ];

  if (loading) {
    return (
      <Modal
        title="Campaign Analytics"
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={null}
        centered
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: '16px' }}>Loading campaign analytics...</Paragraph>
        </div>
      </Modal>
    );
  }

  if (!analytics) {
    return (
      <Modal
        title="Campaign Analytics"
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={null}
        centered
      >
        <Alert
          message="Error Loading Analytics"
          description="Unable to load campaign analytics. Please try again."
          type="error"
          showIcon
          action={
            <Button onClick={handleRefresh} icon={<ReloadOutlined />}>
              Retry
            </Button>
          }
        />
      </Modal>
    );
  }

  const { campaign, statusBreakdown, totalMessages } = analytics;

  return (
    <Modal
      title={
        <Space>
          <BarChartOutlined />
          Campaign Analytics: {campaign.name}
          <Tag color={getStatusColor(campaign.status)}>{campaign.status}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
          {campaign.status === MESSAGE_STATUS.PROCESSING && (
            <Button 
              danger 
              icon={<StopOutlined />}
              onClick={handleCancelCampaign}
              loading={actionLoading}
            >
              Cancel Campaign
            </Button>
          )}
          {statusBreakdown.failed > 0 && (
            <Button 
              type="primary"
              icon={<RedoOutlined />}
              onClick={handleRetryFailed}
              loading={actionLoading}
            >
              Retry Failed ({statusBreakdown.failed})
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Campaign Overview */}
        <Card title="Campaign Overview" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Statistic 
                title="Total Recipients" 
                value={totalMessages} 
                prefix={<MessageOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Messages Sent" 
                value={statusBreakdown.sent} 
                prefix={<SendOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Failed Messages" 
                value={statusBreakdown.failed} 
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Estimated Cost" 
                value={analytics.estimatedCost} 
                prefix={<DollarOutlined />}
                precision={2}
                suffix="USD"
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div>
                <Text strong>Progress: {getProgressPercentage()}%</Text>
                <Progress 
                  percent={getProgressPercentage()} 
                  status={campaign.status === MESSAGE_STATUS.FAILED ? 'exception' : 'active'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>
            </Col>
            <Col span={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Delivery Rate: </Text>
                  <Text strong style={{ color: '#52c41a' }}>
                    {analytics.deliveryRate.toFixed(1)}%
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Failure Rate: </Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {analytics.failureRate.toFixed(1)}%
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Avg. Segments: </Text>
                  <Text strong>{analytics.averageSegments.toFixed(1)}</Text>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Status Breakdown */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col span={12}>
            <Card title="Message Status Breakdown" size="small">
              <Table 
                dataSource={getStatusTableData()}
                columns={statusColumns}
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Location Breakdown" size="small">
              <Table 
                dataSource={getLocationTableData()}
                columns={locationColumns}
                pagination={false}
                scroll={{ y: 200 }}
                size="small"
              />
            </Card>
          </Col>
        </Row>

        {/* Campaign Timeline */}
        {analytics.timeMetrics && (
          <Card title="Campaign Timeline" size="small" style={{ marginBottom: '16px' }}>
            <Timeline>
              {analytics.timeMetrics.queuedAt && (
                <Timeline.Item 
                  dot={<ClockCircleOutlined />}
                  color="blue"
                >
                  <Text strong>Queued: </Text>
                  {analytics.timeMetrics.queuedAt.toDate().toLocaleString()}
                </Timeline.Item>
              )}
              {analytics.timeMetrics.processingStartedAt && (
                <Timeline.Item 
                  dot={<SendOutlined />}
                  color="orange"
                >
                  <Text strong>Processing Started: </Text>
                  {analytics.timeMetrics.processingStartedAt.toDate().toLocaleString()}
                </Timeline.Item>
              )}
              {analytics.timeMetrics.processingEndedAt && (
                <Timeline.Item 
                  dot={<CheckCircleOutlined />}
                  color="green"
                >
                  <Text strong>Processing Completed: </Text>
                  {analytics.timeMetrics.processingEndedAt.toDate().toLocaleString()}
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        )}

        {/* Role Breakdown */}
        {Object.keys(analytics.roleBreakdown).length > 0 && (
          <Card title="Role Distribution" size="small">
            <Row gutter={[8, 8]}>
              {Object.entries(analytics.roleBreakdown)
                .sort(([,a], [,b]) => b - a)
                .map(([role, count]) => (
                <Col span={6} key={role}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic 
                      title={role} 
                      value={count} 
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Campaign Message Preview */}
        {campaign.message && (
          <Card title="Message Content" size="small" style={{ marginTop: '16px' }}>
            <Alert
              message="Message Preview"
              description={
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  marginTop: '8px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap'
                }}>
                  {campaign.message}
                </div>
              }
              type="info"
              showIcon
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                Character count: {campaign.message.length} | 
                SMS segments: {Math.ceil(campaign.message.length / 160)} |
                Cost per recipient: ${(Math.ceil(campaign.message.length / 160) * 0.075).toFixed(3)}
              </Text>
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};

export default BulkSMSAnalyticsDashboard; 